import { Immutable } from "immer";
import React from "react";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";

import { templates } from "@reactivated";
import type {
    CharBlock as CharBlockValue,
    ImageChooserBlock as ImageChooserBlockValue,
    RawHTMLBlock as RawHTMLBlockValue,
    RichTextBlock as RichTextBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { buildToolboxPageMap } from "../../blocks/toolboxPageMap";

/**
 * tempurpedic UtilityPageWithToolbox body assembly (#33588).
 *
 * ``UtilityPageBodyContent`` = the ``Toolbox`` plus the same four PRIMITIVE keys
 * as BasicPage (``heading`` / ``paragraph`` / ``image`` / ``html``) and a
 * re-declared ``promo_conditional_block`` / ``accordion_section`` (both already
 * Toolbox keys). The four primitives are reproduced inline exactly as the
 * BasicPage assembly (Wagtail default ``render_basic``; see that file's
 * docstring for the per-primitive rationale -- esp. ``paragraph`` raw-injects
 * the already-``.rich-text``-wrapped serialized value).
 *
 * WRAPPER PARITY (load-bearing): the legacy ``utility_page_with_toolbox.html``
 * rendered a bare ``{% for block in self.body %}{% include_block block %}
 * {% endfor %}`` loop inside ``<div id="utility-page-with-toolbox">`` (NO
 * per-block Wagtail wrapper), so this renders with ``tagName={React.Fragment}``;
 * the ``#utility-page-with-toolbox`` div stays in the page template wrapping the
 * single ``include_react``.
 *
 * The four page/request/settings-dependent toolbox blocks (and nested ones) are
 * handled by ``buildToolboxPageMap`` via the ``get_toolbox_block_ssr``
 * side-channel.
 */
type IProps = Immutable<
    Pick<
        templates.UtilityPageWithToolboxTemplate,
        | "page"
        | "hardcoded_ssr"
        | "hotspot_ssr"
        | "breadcrumbs_ssr"
        | "customer_service_ssr"
        | "shop_compare_ssr"
        | "tab_feature_attribute_ssr"
        | "reviews_ssr"
    >
>;

export const UtilityPageWithToolboxBody = (
    props: IProps,
): React.JSX.Element => {
    const map = {
        ...buildToolboxPageMap(props),
        // --- UtilityPageBodyContent primitive extras (Wagtail default render_basic) ---
        heading: (block: { value: Immutable<CharBlockValue> }) => (
            <>{block.value}</>
        ),
        paragraph: (block: { value: Immutable<RichTextBlockValue> }) => (
            <div dangerouslySetInnerHTML={{ __html: block.value }} />
        ),
        image: (block: { value: Immutable<ImageChooserBlockValue> }) => (
            <img
                src={block.value.url}
                alt={block.value.default_alt_text}
                width={block.value.width}
                height={block.value.height}
                loading="lazy"
                decoding="async"
            />
        ),
        // `html` (RawHTMLBlock) -> the shared `RawHTMLBlock` component (strips +
        // re-applies the single `.raw-html-block` wrapper `RawHTML.__new__`
        // pre-applies), reproducing the legacy `{% include_block %}` DOM
        // (`.block-html > .raw-html-block`) instead of nesting an extra div.
        // Same entry as the PDP `HTML` key.
        html: (block: { value: Immutable<RawHTMLBlockValue> }) => (
            <RawHTMLBlock value={block.value} />
        ),
    };
    // `body` is non-null (`blank=False`); an empty stream renders nothing.
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.body.value}
            components={map}
        />
    );
};
